
import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { callLegalAssistant } from "../services/aiService";
import { extractTextFromPdf } from "../utils/pdfTextExtractor";
import type { AIMode, AIDataSource, AttachmentRef } from "../types/ai";
import { useToast } from "../components/Toast";

export interface LegalAIContextPayload {
  contextType: "contract" | "case" | "officialDoc" | "sealRequest" | "seal" | "governance" | "template" | "other" | "general" | string;
  contextId?: string;
  contextTitle?: string;
  
  // Data Sources
  currentFields?: any;
  templateText?: string;
  attachments?: AttachmentRef[];
}

export function useLegalAIHelper(
  payload: LegalAIContextPayload | null,
  options?: { 
    onApplyResult?: (target: string, content: string) => void 
  }
) {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [activeMode, setActiveMode] = useState<AIMode>("summary");
  const [dataSource, setDataSource] = useState<AIDataSource>("attachments");
  
  const [notes, setNotes] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [responseText, setResponseText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const openHelper = (mode: AIMode = "summary") => {
    setActiveMode(mode);
    setIsOpen(true);
    setErrorMessage("");
    setResponseText("");
  };

  const closeHelper = () => {
    setIsOpen(false);
  };

  const callAI = async (tempFiles?: AttachmentRef[]) => {
    // 1. Basic Check
    if (!user) {
      showToast("請先登入", "error");
      return;
    }

    setErrorMessage("");
    setResponseText("");
    setIsLoading(true);

    try {
        // 2. Prepare Attachments
        const effectiveAttachments = [
          ...(payload?.attachments || []),
          ...(tempFiles || [])
        ];

        // Sanitize: Ensure name exists for backend
        const sanitizedAttachments = effectiveAttachments.map(f => {
             let name = f.name;
             if (!name && f.storagePath) {
                 const parts = f.storagePath.split('/');
                 name = parts[parts.length - 1];
             }
             return { ...f, name: name || "unknown_file" };
        });

        // If mode requires attachments, check availability
        if (dataSource === 'attachments' && sanitizedAttachments.length === 0 && !notes) {
            throw new Error("請上傳檔案或輸入指令。");
        }

        // 3. Auto-Inject Default Prompt if empty
        let userInstruction = notes.trim();
        if (!userInstruction && sanitizedAttachments.length > 0) {
            switch (activeMode) {
                case "summary":
                    userInstruction = "請輸出『管理報告版 + 務實法務版』兩段（管理報告版放上方）。管理報告版 10 行內：用 5–8 點交代重點、風險、需決策點、下一步。務實法務版：用 10–15 點條列，依文件內容摘要：目的/背景、服務/標的、雙方主要義務、付款/請款節點、交付/驗收、期間/終止、保密/個資、責任/賠償/違約金、智財/素材、準據法/管轄。未提及者請寫「未見」。";
                    break;
                case "analysis":
                    userInstruction = "請輸出『管理報告版 + 務實法務版』兩段（管理報告版放上方）。以「某某公司（甲方）」角度，管理報告版請在 10 行內交代：重大風險/缺漏、影響、需決策點、下一步。務實法務版再完整條列：重大風險/缺漏（含條款關鍵字或位置線索）＋具體進攻談判/補強建議（含可直接使用話術）。至少涵蓋：付款、履約/驗收、終止、違約金/賠償、保密/個資、智財/素材、競業/不招攬、責任限制、準據法/管轄。";
                    break;
                case "risk":
                    userInstruction = "請輸出『管理報告版 + 務實法務版』兩段（管理報告版放上方）。管理報告版：用 5–8 點列出最高優先風險與需決策/授權。務實法務版：做風險矩陣（高/中/低），每項包含『風險點 → 為何是風險 → 可能損失/影響 → 進攻談判/補強對策 → 優先順序』。若資料不足，請先寫限制再給目前可判斷的結果。";
                    break;
                case "decision":
                    userInstruction = "請輸出『管理報告版 + 務實法務版』兩段（管理報告版放上方）。管理報告版：用 5–8 點列出最關鍵決策點與建議立場。務實法務版：整理成三類『可接受』『需談判』『不可接受』的決策點；每點提供 1 句進攻談判話術 + 1 句底線/替代方案。務必以文件內容為準，未提及者寫「未見」。";
                    break;
                case "draft":
                    userInstruction = "請輸出『管理報告版 + 務實法務版』兩段（管理報告版放上方）。務實法務版請依文件內容草擬可直接貼進合約的修文：分「建議新增」/「建議修改」兩段，提供條文（中文）並註記目的/風險對應；並加上一句可直接用的進攻談判話術（每條 1 句）。未提及的條款不可憑空新增，需先標註「現行未見」。";
                    break;
            }
        }

        // 4. Determine Context (Fallback to GENERAL if missing)
        const cType = (payload?.contextType || "GENERAL").toUpperCase();
        const cId = payload?.contextId || undefined; // Let backend handle null/undefined

        console.log(`[AI Helper] Calling with Mode: ${activeMode}, Files: ${sanitizedAttachments.length}`);

        // 5. Call Unified Service

        // 4.5 Extract attachment text on client (supports local OCR PDFs without upload)
        let extractedText: string | undefined = undefined;

        // (A) Prefer pre-extracted text if caller provided it (e.g., local OCR)
        const preParts = sanitizedAttachments
          .map((a: any) => {
            const t = String(a?.extractedText || "").trim();
            if (!t) return "";
            const n = String(a?.name || "unknown_file");
            return `【附件：${n}】\n${t}`;
          })
          .filter(Boolean);

        // (B) If no pre-extracted text, fallback to light PDF text-layer extraction via downloadUrl
        const parts: string[] = [...preParts];
        const pdfFiles = sanitizedAttachments.filter(
          (a: any) => String(a?.name || "").toLowerCase().endsWith(".pdf") && a?.downloadUrl && !a?.extractedText
        );
        if (pdfFiles.length > 0) {
          try {
            const take = pdfFiles.slice(0, 1); // keep it light: only first PDF
            for (const att of take) {
              const url = String(att.downloadUrl);
              const resp = await fetch(url);
              const blob = await resp.blob();
              const file = new File([blob], att.name || "attachment.pdf", { type: blob.type || "application/pdf" });
              const text = await extractTextFromPdf(file);
              if (text && text.trim()) {
                parts.push(`【PDF：${att.name}】\n${text}`);
              }
            }
          } catch (e) {
            console.warn("[AI Helper] PDF extract failed:", e);
          }
        }

        if (parts.length > 0) extractedText = parts.join("\n\n");

        const res = await callLegalAssistant({
            mode: activeMode,
            attachments: sanitizedAttachments, // Pass array
            contextType: cType,
            contextId: cId,
            userInstruction: userInstruction ,
            extractedText
        });

        // 6. Handle Response
        if (!res.ok) {
            throw new Error(res.message || "AI 服務回傳未知的錯誤。");
        } 
        
        if (!res.text) {
            throw new Error("AI 回傳內容為空，請稍後再試。");
        }

        setResponseText(res.text);

    } catch (e: any) {
        console.error("[AI Helper] Error:", e);
        setErrorMessage(e.message || "發生未預期錯誤，請檢查網路或權限。");
        showToast(`AI 分析發生錯誤: ${e.message}`, "error");
    } finally {
        setIsLoading(false);
    }
  };

  const applyResultToField = (target: string) => {
    if (options?.onApplyResult && responseText) {
      options.onApplyResult(target, responseText);
      showToast(`已寫入 ${target}`);
    } else {
      showToast("無法寫入：未設定回調或無內容", "error");
    }
  };

  return {
    isOpen,
    openHelper,
    closeHelper,
    activeMode,
    setActiveMode,
    dataSource,
    setDataSource,
    notes,
    setNotes,
    isLoading,
    responseText,
    errorMessage,
    callAI,
    applyResultToField,
    canUseFields: !!payload?.currentFields,
    canUseTemplate: !!payload?.templateText,
    canUseAttachments: true
  };
}